import { useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLeads } from '@/hooks/useLeads';
import { HeroNumber } from '@/components/HeroNumber';
import { FileText, CheckCircle, Euro, MapPin, Clock } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const OFFERTE_STATUSES = ['offerte_verstuurd', 'offerte_herzien', 'akkoord', 'getekend', 'aanbetaling_gevraagd', 'aanbetaling_ontvangen', 'werkbezoek_gepland', 'werkbezoek_gedaan', 'materiaal_besteld', 'levering_gepland', 'geleverd', 'afgerond'];
const AKKOORD_STATUSES = ['akkoord', 'getekend', 'aanbetaling_gevraagd', 'aanbetaling_ontvangen', 'werkbezoek_gepland', 'werkbezoek_gedaan', 'materiaal_besteld', 'levering_gepland', 'geleverd', 'afgerond'];
const BETAALD_STATUSES = ['aanbetaling_ontvangen', 'werkbezoek_gepland', 'werkbezoek_gedaan', 'materiaal_besteld', 'levering_gepland', 'geleverd', 'afgerond'];

export default function PartnerBillingPage() {
  const { effectivePartnerId } = useAuth();
  const { leads } = useLeads(effectivePartnerId);

  const data = useMemo(() => {
    const offertes = leads.filter(l => OFFERTE_STATUSES.includes(l.status));
    const akkoord = leads.filter(l => AKKOORD_STATUSES.includes(l.status));
    const betaald = leads.filter(l => BETAALD_STATUSES.includes(l.status));
    const totalOmzet = leads
      .filter(l => l.status === 'afgerond')
      .reduce((s, l) => s + (l.estimated_max_eur || 0), 0);

    const recentLeads = [...leads]
      .filter(l => l.estimated_max_eur != null && l.estimated_max_eur > 0)
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      .slice(0, 10);

    return { offertes, akkoord, betaald, totalOmzet, recentLeads };
  }, [leads]);

  return (
    <div className="space-y-8 max-w-4xl mx-auto animate-fade-up">
      <div className="stagger-1">
        <h1 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>Omzet</h1>
        <p className="text-sm mt-1" style={{ color: 'var(--text-tertiary)' }}>Financieel overzicht van jouw leads</p>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 stagger-2">
        <div className="p-5 rounded-xl" style={{ background: 'var(--bg-surface-raised)', border: '1px solid var(--border-primary)' }}>
          <div className="w-8 h-8 rounded-lg flex items-center justify-center mb-3" style={{ background: 'var(--bg-input)', color: 'var(--text-tertiary)', border: '1px solid var(--border-primary)' }}>
            <FileText size={16} />
          </div>
          <p className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>{data.offertes.length}</p>
          <p className="text-[11px] uppercase tracking-wider mt-1" style={{ color: 'var(--text-muted)' }}>Offertes verstuurd</p>
        </div>
        <div className="p-5 rounded-xl" style={{ background: 'var(--bg-surface-raised)', border: '1px solid var(--border-primary)' }}>
          <div className="w-8 h-8 rounded-lg flex items-center justify-center mb-3" style={{ background: 'var(--accent-dim)', color: 'var(--accent)', border: '1px solid var(--accent-glow)' }}>
            <CheckCircle size={16} />
          </div>
          <p className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>{data.akkoord.length}</p>
          <p className="text-[11px] uppercase tracking-wider mt-1" style={{ color: 'var(--text-muted)' }}>Akkoord</p>
        </div>
        <div className="p-5 rounded-xl" style={{ background: 'var(--bg-surface-raised)', border: '1px solid var(--border-primary)' }}>
          <div className="w-8 h-8 rounded-lg flex items-center justify-center mb-3" style={{ background: 'var(--success-bg)', color: 'var(--success-text)', border: '1px solid var(--success-border)' }}>
            <Euro size={16} />
          </div>
          <p className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>{data.betaald.length}</p>
          <p className="text-[11px] uppercase tracking-wider mt-1" style={{ color: 'var(--text-muted)' }}>Betaald (aanbetaling)</p>
        </div>
        <div style={{
          padding: '20px',
          background: data.totalOmzet > 0 ? 'var(--accent-dim)' : 'var(--bg-surface-raised)',
          border: `1px solid ${data.totalOmzet > 0 ? 'var(--accent)' : 'var(--border-primary)'}`,
          borderRadius: '12px',
        }}>
          <HeroNumber
            label="Totaal omzet"
            value={data.totalOmzet > 0 ? `€${data.totalOmzet.toLocaleString('nl-NL')}` : '—'}
            size="lg"
            accent={data.totalOmzet > 0}
          />
        </div>
      </div>

      {/* Recent leads with value */}
      <div className="rounded-xl overflow-hidden stagger-3" style={{ background: 'var(--bg-surface-raised)', border: '1px solid var(--border-primary)' }}>
        <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: 'var(--border-separator)' }}>
          <h2 className="text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>Recente leads</h2>
          <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>{data.recentLeads.length} met waarde</span>
        </div>
        <div className="divide-y" style={{ borderColor: 'var(--border-separator)' }}>
          {data.recentLeads.length === 0 ? (
            <div className="px-5 py-8 text-center">
              <Euro size={24} className="mx-auto mb-2" style={{ color: 'var(--text-muted)' }} />
              <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>Nog geen leads met geschatte waarde</p>
            </div>
          ) : (
            data.recentLeads.map(lead => (
              <div key={lead.id} className="px-5 py-3.5 flex items-center justify-between transition-colors hover:bg-[var(--bg-surface-hover)]"
              >
                <div className="min-w-0">
                  <p className="text-[13px] font-medium" style={{ color: 'var(--text-secondary)' }}>{lead.firstname} {lead.lastname}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[11px] flex items-center gap-1" style={{ color: 'var(--text-muted)' }}>
                      <MapPin size={9} />{lead.city}
                    </span>
                    <span className="text-[11px] flex items-center gap-1" style={{ color: 'var(--text-muted)' }}>
                      <Clock size={9} />{formatDistanceToNow(new Date(lead.created_at), { addSuffix: true })}
                    </span>
                  </div>
                </div>
                <div className="text-right shrink-0 ml-4">
                  <p className="text-[13px] font-semibold" style={{ color: 'var(--accent)' }}>€{(lead.estimated_max_eur || 0).toLocaleString('nl-NL')}</p>
                  <p className="text-[11px] mt-0.5" style={{ color: 'var(--text-muted)' }}>Schatting</p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
